import { useCallback, useEffect, useRef, useState } from 'react';
import { toZwApiError, type ZwApiError } from '../api/client';
import type { Hold, HoldRequest } from '../api/types';
import { useZeitwahl } from '../provider';
import { useSessionId } from './use-session-id';

export type HoldStatus = 'idle' | 'creating' | 'held' | 'expired' | 'error';

export interface UseHoldResult {
  hold: Hold | null;
  status: HoldStatus;
  error: ZwApiError | null;
  sessionId: string;
  create: (input: Omit<HoldRequest, 'sessionId'>) => Promise<Hold>;
  /** Releases the current hold server-side (best effort) and clears it locally. */
  release: () => Promise<void>;
  /** Marks the hold expired locally (the countdown reached zero). */
  expire: () => void;
  /** Forgets the hold without a release call (after a successful booking). */
  reset: () => void;
}

interface HoldState {
  hold: Hold | null;
  status: HoldStatus;
  error: ZwApiError | null;
}

const idle: HoldState = { hold: null, status: 'idle', error: null };

export function useHold(org: string): UseHoldResult {
  const { api } = useZeitwahl();
  const sessionId = useSessionId();
  const [state, setState] = useState<HoldState>(idle);
  const holdRef = useRef<Hold | null>(null);
  const seq = useRef(0);

  const create = useCallback<UseHoldResult['create']>(
    async (input) => {
      const n = ++seq.current;
      holdRef.current = null;
      setState({ hold: null, status: 'creating', error: null });
      try {
        const hold = await api.createHold(org, { ...input, sessionId });
        // A newer request superseded this one; the server already released it via the sessionId rule.
        if (n !== seq.current) return hold;
        holdRef.current = hold;
        setState({ hold, status: 'held', error: null });
        return hold;
      } catch (error) {
        const err = toZwApiError(error);
        if (n === seq.current) setState({ hold: null, status: 'error', error: err });
        throw err;
      }
    },
    [api, org, sessionId]
  );

  const release = useCallback(async () => {
    const current = holdRef.current;
    holdRef.current = null;
    seq.current++;
    setState(idle);
    if (!current) return;
    try {
      await api.releaseHold(org, current.id, sessionId);
    } catch {
      // The hold expires on its own; nothing to surface.
    }
  }, [api, org, sessionId]);

  const expire = useCallback(() => {
    holdRef.current = null;
    setState((s) => ({ ...s, status: 'expired' }));
  }, []);

  const reset = useCallback(() => {
    holdRef.current = null;
    seq.current++;
    setState(idle);
  }, []);

  // An abandoned widget frees its slot immediately instead of waiting for the TTL.
  useEffect(
    () => () => {
      const current = holdRef.current;
      if (current) api.releaseHold(org, current.id, sessionId).catch(() => undefined);
    },
    [api, org, sessionId]
  );

  return { ...state, sessionId, create, release, expire, reset };
}
